import React, { useState, useEffect } from "react";
import axios from "axios";
import { Heart, Clock } from "lucide-react";

import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useSettings } from "../context/SettingsContext";
import MenuTags from "./MenuTags";

const MenuItemGridView = ({ items, onView, selectedCatId , onRefreshCategories, firstItemRef}) => {
  const DAY_LETTER_MAP = {
  "0": "Sun",
  "1": "Mon",
  "2": "Tue",
  "3": "Wed",
  "4": "Thu",
  "5": "Fri",
  "6": "Sat",
};

const getDiscountedPrice = (item, timeZone = "Asia/Kolkata") => {
  if (!item?.is_special) return null;

  let applyDiscount = false;

  // -----------------------------
  // Wise 1 → Always apply
  // -----------------------------
  if (item.wise == 1) {
    applyDiscount = true;
  }

  // -----------------------------
  // Wise 2 → Day & Time based
  // -----------------------------
  if (item.wise == 2) {
    const now = new Date();

    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const parts = formatter.formatToParts(now);

    const dayMap = {
      Sun: "0",
      Mon: "1",
      Tue: "2",
      Wed: "3",
      Thu: "4",
      Fri: "5",
      Sat: "6",
    };

    const currentDay =
      dayMap[parts.find((p) => p.type === "weekday")?.value];

    const hour = parts.find((p) => p.type === "hour")?.value;
    const minute = parts.find((p) => p.type === "minute")?.value;

    const currentTime = `${hour}:${minute}`;

    const discountDays = item.discount_days?.split(",") || [];

    if (discountDays.includes(currentDay)) {
      if (item.is_time == 1) {
        const from = item.from_time_av?.slice(0, 5);
        const to = item.to_time_av?.slice(0, 5);

        if (currentTime >= from && currentTime <= to) {
          applyDiscount = true;
        }
      } else {
        applyDiscount = true;
      }
    }
  }

  if (!applyDiscount) return null;

  const originalPrice = parseFloat(item.price);
  let discountedPrice = originalPrice;

  if (item.discount_type === "F") {
    discountedPrice = originalPrice - parseFloat(item.discount);
  }

  if (item.discount_type === "P") {
    discountedPrice =
      originalPrice -
      (originalPrice * parseFloat(item.discount)) / 100;
  }

  return discountedPrice > 0 ? discountedPrice : 0;
};


const getAvailabilityUI = (item) => {
  if (!item?.day) return null;

  const allDays = ["0", "1", "2", "3", "4", "5", "6"];
  const daysArray = item.day.split(",");

  const isAllDays =
    allDays.length === daysArray.length &&
    allDays.every((d) => daysArray.includes(d));

  const from = item.from_time?.slice(0, 5);
  const to = item.to_time?.slice(0, 5);

  const isFullDay = from === "00:00" && to === "23:59";

  // RULE 1: All days + full day → show nothing
  if (isAllDays && isFullDay) return null;

  // RULE 2: All days + specific hours → show time only
  if (isAllDays && !isFullDay) {
    return {
      days: null,
      time: `${from} – ${to}`,
    };
  }

  const dayLabels = daysArray
    .map((d) => DAY_LETTER_MAP[d])
    .filter(Boolean);

  return {
    days: dayLabels,
    time: isFullDay ? null : `${from} – ${to}`,
  };
};


  const [favorites, setFavorites] = useState([]);
  const user = useSelector((state) => state.auth.user);
const { settings } = useSettings();
  // ✅ Fetch user favorites from your new API
  useEffect(() => {
    if (user && user.id) {
      axios
        .get(`${window.BASE_API_URL}getuserfav/${user.id}`)
        .then((res) => {
          if (res.data) {
            // expecting backend to return array of IDs
            setFavorites(res.data.map((fav) => fav.menu_id));
          }
        })
        .catch((err) => {
          console.error("Failed to fetch favorites:", err);
        });
    }
  }, [user]);

  // ✅ Toggle favorite
  const handleFavoriteToggle = async (itemId) => {
    if (!user) {
      toast.warn("Please login to add favorites.");
      return;
    }

    const isFav = favorites.includes(itemId);
    const payload = {
      id: itemId,
      selectedCatId: selectedCatId,
      user_id: user.id,
    };

    try {
      if (isFav) {
        await axios.post(`${window.BASE_API_URL}remove-favorite`, payload);
        setFavorites((prev) => prev.filter((id) => id !== itemId));
        toast.info("Removed from favorites.");
        onRefreshCategories();
      } else {
        await axios.post(`${window.BASE_API_URL}add-favorite`, payload);
        setFavorites((prev) => [...prev, itemId]);
        toast.success("Added to favorites!");
        onRefreshCategories();
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Try again!");
    }
  };
const isItemAvailableNow = (item, timeZone = "Asia/Kolkata") => {
  if (!item?.day) return true;

  const now = new Date();

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(now);

  const dayMap = {
    Sun: "0",
    Mon: "1",
    Tue: "2",
    Wed: "3",
    Thu: "4",
    Fri: "5",
    Sat: "6",
  };

  const currentDay = dayMap[
    parts.find((p) => p.type === "weekday")?.value
  ];

  const hour = parts.find((p) => p.type === "hour")?.value;
  const minute = parts.find((p) => p.type === "minute")?.value;

  const currentTime = `${hour}:${minute}`;

  const daysArray = item.day.split(",");

  if (!daysArray.includes(currentDay)) return false;

  const from = item.from_time?.slice(0, 5);
  const to = item.to_time?.slice(0, 5);

  if (!from || !to) return true;

  return currentTime >= from && currentTime <= to;
};



  if (!items || items.length === 0) return null;

  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 p-4">
      {items.map((item, index) => {
        const imageUrl = item.image;
        const isFav = favorites.includes(item.id);
        const availability = getAvailabilityUI(item);
        const isTimeAvailable = isItemAvailableNow(
  item,
  settings?.timezone || "Asia/Kolkata"
);
const discountedPrice = getDiscountedPrice(
  item,
  settings?.timezone || "Asia/Kolkata"
);

const isCategoryActive =
  !item?.category || item?.category?.status == 1;

  const isAvailable = isTimeAvailable && isCategoryActive &&  item?.status == 1;;

        return (
          <div
            className="menu-card bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
            key={item.id}
             ref={index === 0 ? firstItemRef : null}
          >
            {/* Image + Heart */}
           <div className="relative w-full h-[170px] overflow-hidden imgdiv">
  <div
    className="w-full h-full bg-center bg-cover"
    style={{ backgroundImage: `url(${imageUrl})` }}
  ></div>

  {/* Clock Icon for Custom Prep Items */}
  {item?.is_custom_prep_time == 1 && (
    <div className="absolute top-2 left-2 group">
      <div className="bg-white/90 p-1.5 rounded-full shadow-md">
        <Clock size={20} className="text-orange-600" />
      </div>
      <div
        className="absolute left-0 mt-1 w-max opacity-0 group-hover:opacity-100 
                  transition-opacity duration-200 bg-gray-800 text-white text-xs 
                  rounded px-2 py-1"
      >
        This item has extended prep time
      </div>
    </div>
  )}

  {/* 🔥 Availability Overlay (on image bottom) */}
{availability && (
  <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-1 z-0">
    {/* Days */}
    {availability.days &&
      availability.days.map((day, i) => (
        <span
          key={i}
          className="px-2 py-0.5 text-[10px] font-semibold
                     rounded-full bg-black/70 text-white
                     backdrop-blur-md"
        >
          {day}
        </span>
      ))}

    {/* Time */}
    {availability.time && (
      <span
        className="flex items-center gap-1 px-2 py-0.5 text-[10px]
                   rounded-full bg-orange-500/80 text-white
                   backdrop-blur-md"
      >
        <Clock size={10} />
        {availability.time}
      </span>
    )}
  </div>
)}


  {(user &&
    selectedCatId != "combo" &&
    selectedCatId != "free_item" &&
    selectedCatId != "first_offer" &&
    !String(selectedCatId || "").startsWith("free_")) && (
    <button
      onClick={() => handleFavoriteToggle(item.id)}
      className="absolute top-2 right-2 bg-white/80 hover:bg-white p-1.5 rounded-full shadow-md z-0"
    >
      <Heart
        size={22}
        className={`transition-colors duration-200 ${
          isFav ? "text-red-500 fill-red-500" : "text-gray-500"
        }`}
      />
    </button>
  )}
</div>


            {/* Content */}
            <div className="pl-3 pr-3 pb-3">
              <h3 className="text-lg font-semibold mb-1"  style={{ color: settings?.cart_title_color || "#111827" }}>{item.name}</h3>
              <MenuTags tags={item.tags} />

 

              <p className="desc text-gray-600 mb-4 line-clamp-2">
                {item.description}
              </p>

              <div className="price-row flex justify-between items-center">
                {/* <span
                  className={`font-bold ${
                    item.is_price == 1 ? "underline decoration-grey-500" : ""
                  }`}
                >
                  {localStorage.getItem("currency")} {item.price.toFixed(2)}

                  
                </span> */}

<span
  className={`font-bold flex items-center gap-2 ${
    item.is_price == 1 ? "underline decoration-grey-500" : ""
  }`}
>
  {discountedPrice !== null ? (
    <>
      {/* Original Price */}
      <span className="text-gray-400 line-through text-sm">
        {localStorage.getItem("currency")}{" "}
        {parseFloat(item.price).toFixed(2)}
      </span>

      {/* Discounted Price */}
      <span className=" text-lg font-bold">
        {localStorage.getItem("currency")}{" "}
        {discountedPrice.toFixed(2)}
      </span>
    </>
  ) : (
    <>
      {localStorage.getItem("currency")}{" "}
      {parseFloat(item.price).toFixed(2)}
    </>
  )}
</span>

           <div className="relative group">
  <button
    disabled={!isAvailable}
    onClick={() => isAvailable && onView(item.id)}
    style={{
      background: isAvailable
        ? settings?.cart_btn_color || "#000000ff"
        : "#e5e7eb", // gray-200 when disabled
      color: isAvailable
        ? settings?.cart_btn_text_color || "#ffffff"
        : "#9ca3af", // gray-400
      cursor: isAvailable ? "pointer" : "not-allowed",
    }}
    onMouseEnter={(e) => {
      if (isAvailable) {
        e.currentTarget.style.background =
          settings?.cart_btn_hover || "#f59e0b";
      }
    }}
    onMouseLeave={(e) => {
      if (isAvailable) {
        e.currentTarget.style.background =
          settings?.cart_btn_color || "#ffffff";
      }
    }}
    className={`px-5 py-2 bg-gradient-to-r to-indigo-600 text-white text-sm 
      font-medium rounded-full shadow-lg transform transition-all duration-300
      focus:ring-2 focus:outline-none
      ${!isAvailable ? "opacity-70 hover:scale-100 hover:shadow-lg" : "hover:scale-105 hover:shadow-xl"}`}
  >
    {isAvailable ? "View" : "Unavailable"}
  </button>

  {/* 🔥 Hover tooltip (only when unavailable) */}
  {/* {!isAvailable && availability && (
    <div
      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2
                 opacity-0 group-hover:opacity-100 transition-all duration-300
                 pointer-events-none z-50"
    >
      <div
        className="px-4 py-2 text-xs rounded-xl shadow-xl
                   bg-black/80 text-white backdrop-blur-md
                   flex items-center gap-2 whitespace-nowrap"
      >
        {availability.days && (
          <span className="font-semibold tracking-widest">
            {availability.days}
          </span>
        )}

        {availability.time && (
          <span className="flex items-center gap-1 text-orange-300">
            <Clock size={12} />
            {availability.time}
          </span>
        )}
      </div>
    </div>
  )} */}
</div>

              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
};

export default MenuItemGridView;
